import { Mail, Phone, Globe } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

import { ShareArticleSection } from "../ShareArticleSection";

const AmazonConnectLaunch = () => {
  return (
    <div className="modal-content-body">
      <ShareArticleSection />
      <div className="modal-section">
        <div className="flex-content">
          <div>
            <h3>Use Case</h3>
          </div>
          <div>
            <p>
              Rapid migration or greenfield deployment of an AWS-native,
              omnichannel contact centre enabling organisations to quickly move
              from legacy/on-premise systems or to launch new digital customer
              support operations from scratch.
            </p>
          </div>
        </div>

        <div className="flex-content">
          <div>
            <h3>Solution</h3>
          </div>
          <div>
            <a
              href="https://aws.amazon.com/marketplace/pp/prodview-kp45qe3zkckqo?sr=0-4&ref_=beagle&applicationId=AWSMPContessa"
              target="_blank"
              rel="noopener noreferrer"
            >
              Amazon Connect Launch
            </a>
          </div>
        </div>

        <div className="flex-content">
          <div>
            <h3>Target Segment</h3>
          </div>
          <div>
            <ul>
              <li>
                SMB and Enterprise customers launching new brands, locations, or
                contact centre teams
              </li>
              <li>
                Organisations undertaking digital transformation, rapid
                modernisation, or expansion
              </li>
              <li>
                Greenfield scenarios or those requiring fast time-to-value for
                new customer service capabilities
              </li>
            </ul>
          </div>
        </div>

        <div className="flex-content">
          <div>
            <h3>Target Personas</h3>
          </div>
          <div>
            <p>
              CTO, Head of CX, IT Operations Manager, Contact Centre Director,
              CFO, Digital Transformation Lead
            </p>
          </div>
        </div>
      </div>

      {/* Opportunity & Qualification */}
      <div className="modal-section">
        <h2 className="modal-section-title">Opportunity and Qualification</h2>
        <div className="modal-section-grid">
          <div className="modal-column">
            <h3 className="modal-subtitle">
              What is the customer trying to accomplish?
            </h3>
            <ul>
              <li>Enable omnichannel (voice, chat, email, SMS)</li>
              <li>Launch a new, scalable customer support operation</li>
              <li>Integrate with CRM and digital systems</li>
              <li>Reduce costs and shift to pay-as-you-go</li>
              <li>Support remote, multi-site, or fast growth</li>
              <li>Boost self-service and automation</li>
              <li>Gain real-time analytics and insights</li>
            </ul>
          </div>

          <div className="modal-column">
            <h3 className="modal-subtitle">Key Words/ Questions</h3>
            <ul>
              <li>Migrating from Avaya, Genesys, or legacy systems</li>
              <li>Need a rapid cloud contact centre deployment</li>
              <li>Adding chat, SMS, or email channels</li>
              <li>Current system can't scale or is too expensive</li>
              <li>Supporting remote or hybrid agents</li>
              <li>Seeking unified digital and voice experience</li>
              <li>Wanting more self-service or automation</li>
              <li>Struggling with seasonal or rapid growth</li>
            </ul>
          </div>

          <div className="modal-column">
            <h3 className="modal-subtitle">Pain Point/ Challenge</h3>
            <ul>
              <li>High capex and complexity in legacy systems</li>
              <li>Siloed or inconsistent customer service across channels</li>
              <li>
                Slow onboarding/expansion for new services, seasonal peaks, or
                brands
              </li>
              <li>Inability to leverage new AWS features quickly</li>
              <li>High capex/complexity from legacy systems</li>
            </ul>
          </div>

          <div className="modal-column">
            <h3 className="modal-subtitle">Qualifying Questions</h3>
            <ul>
              <li>
                Would you like to see how a cloud-native contact centre could
                improve your customer experience and reduce costs?
              </li>
              <li>
                Are you curious how quickly your team could launch new support
                channels or work remotely with AWS?
              </li>
              <li>
                Would you like to explore how AI and automation can make life
                easier for your agents and customers?
              </li>
              <li>Would a personalised consult on rapid, scalable CX</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Proposed Solution */}
      <div className="modal-section">
        <h2 className="modal-section-title">Proposed Solution / Value</h2>
        <div className="flex-content-half">
          <div>
            <h3>Solution Description</h3>
            <p>
              iCXeed's Amazon Connect Launch delivers a “Blueprint” deployment
              of AWS's flagship cloud contact centre platform, tailored to
              unique requirements and ready for rapid expansion. Includes:
            </p>
            <ul>
              <li>
                Full discovery, migration, design, and operational build by AWS
                specialist experts
              </li>
              <li>
                Optional channel setup (voice, chat, email, SMS), advanced
                feature configuration (IVR, routing, reporting)
              </li>
              <li>
                Integration with CRM, workforce management, and digital assets
              </li>
              <li>
                “Day 1 ready” for advanced analytics, agent assist, and
                AI-powered customer journeys
              </li>
            </ul>
          </div>
          <div>
            <h3>Solution Benefits to AWS Customers</h3>
            <ul>
              <li>
                Fastest path to AWS-native, cloud-first customer service (go
                live in weeks, not months)
              </li>
              <li>
                Resilient, scalable, cost-predictable platform built for hybrid
                and remote workforces
              </li>
              <li>
                Out-of-the-box omnichannel and digital self-service capability
              </li>
              <li>
                Embedded support for future AWS innovations (AI/ML, advanced
                analytics, automated QA)
              </li>
              <li>
                Removes legacy tech debt—reduces time to innovation and unlocks
                full AWS ecosystem from day one
              </li>
            </ul>
          </div>
        </div>

        <h2 className="modal-section-title" style={{ marginTop: "30px" }}>
          Objection Handling
        </h2>
        <div className="flex-content-half">
          <div>
            <p>
              <strong>Concern: Migration Disruption | </strong> Our proven
              blueprint delivers smooth, low-risk transitions (phased or
              parallel cutover), ensuring there's no business downtime.
            </p>

            <p>
              <strong>Cloud Is Too Complex | </strong> Amazon Connect is
              user-friendly, and our partner specialists handle setup, training,
              and ongoing support — no deep IT skills needed.
            </p>
            <p>
              <strong>Cost Savings Doubt | </strong> With AWS pay-as-you-go
              pricing and automation features, most clients see substantial cost
              reductions compared to legacy tech from day one.
            </p>
            <p>
              <strong>"We're Happy With Status Quo” | </strong>Let's benchmark
              your CX performance and potential - five minutes could reveal some
              valuable efficiency and innovation opportunities.
            </p>
            <p>
              <strong>Security or Compliance Worries |</strong> AWS-native
              platforms deliver strong compliance, uptime, and data protection -
              trusted by top banks, healthcare, and governm
            </p>
          </div>
        </div>
      </div>

      <div className="modal-footnote" style={{ border: 0 }}>
        <h3>Primary Partner Contact:</h3>
        <div className="flex-box-blk">
          <div>
            <img
              src="https://www.icxeed.ai/wp-content/themes/icxeed-website-2025/portal-assets/images/thumbnails/ryan-thumbnail.png"
              alt="ryan rayner"
            />
          </div>
          <div>
            <h2>Ryan Rayner</h2>
            <p>Co-Founder & Chief Customer Officer</p>
          </div>
        </div>
        <div className="flex-box-blk footnote-flex-box">
          <div className="flex-box-blk">
            <div>
              <Mail size={20} color="#01C2F3" />
            </div>
            <div>
              <a href="mailto:ryan.rayner@icxeed.ai">ryan.rayner@icxeed.ai</a>
            </div>
          </div>
          <div className="flex-box-blk">
            <div>
              <Phone size={20} color="#01C2F3" />
            </div>
            <div>
              <a href="tel:+61438286198">+61 438 286 198</a>
            </div>
          </div>
        </div>

        <div className="flex-box-blk footnote-flex-box">
          <div className="flex-box-blk">
            <div>
              <FontAwesomeIcon icon={faLinkedinIn} size="lg" color="#01C2F3" />
            </div>
            <div>
              <a
                href=" https://www.linkedin.com/in/ryanrayner/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.linkedin.com/in/ryanrayner/
              </a>
            </div>
          </div>
          <div className="flex-box-blk">
            <div>
              <Globe size={20} color="#01C2F3" />
            </div>
            <div>
              <a
                href="https://icxeed.ai/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://icxeed.ai/
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmazonConnectLaunch;
